import React, { useContext, useState } from 'react'
import SparkLineTable from 'charts/sparkline-table'
import TableFooter from 'shared/tables/table-footer'
import TableLoading from 'shared/tables/table-loading'
import RiskBadge from 'shared/badges/risk-badge'
import { noDigitMoney } from 'helpers/utils'
import { useVendors } from 'api/hooks/use-vendors'
import { useEffect } from 'react'
import SortablePopoverHeader from 'shared/tables/sortable-popover-header'
import StatusBadge from 'shared/badges/status-badge'
import { faCreditCard } from '@fortawesome/free-solid-svg-icons'
import { faClone } from '@fortawesome/free-regular-svg-icons'
import { useNavigate } from 'react-router-dom'
import VendorAttribute from 'shared/badges/vendor-attribute'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import UserContext from 'context/user-context'

function Table({ setFilterParams, filterParams, debouncedSearch }) {
  const navigate = useNavigate()
  const { municipality } = useContext(UserContext)
  const [activePopoverId, setActivePopoverId] = useState(null)

  useEffect(() => {
    setFilterParams((filterParams) => ({
      ...filterParams,
      search: debouncedSearch,
      page: 1
    }))
  }, [debouncedSearch, setFilterParams])

  const {
    isSuccess,
    isLoading,
    data: vendors
  } = useVendors({ params: filterParams })

  return (
    <>
      <SparkLineTable>
        <thead>
          <tr className="text-primary">
            <SortablePopoverHeader
              title="Name"
              filters={filterParams}
              setFilters={setFilterParams}
              column="name"
              popoverId="vendor-name-sort"
              activePopoverId={activePopoverId}
              setActivePopoverId={setActivePopoverId}
            />
            <SortablePopoverHeader
              title="Risk"
              filters={filterParams}
              setFilters={setFilterParams}
              column="risk_rate"
              popoverId="vendor-risk-sort"
              activePopoverId={activePopoverId}
              setActivePopoverId={setActivePopoverId}
            />
            <SortablePopoverHeader
              title="Analytics"
              filters={filterParams}
              setFilters={setFilterParams}
              column="analytics_count"
              popoverId="vendor-analytics-sort"
              activePopoverId={activePopoverId}
              setActivePopoverId={setActivePopoverId}
            />
            <SortablePopoverHeader
              title="Transactions"
              filters={filterParams}
              setFilters={setFilterParams}
              column="transactions_count"
              popoverId="vendor-transactions-sort"
              activePopoverId={activePopoverId}
              setActivePopoverId={setActivePopoverId}
            />
            <SortablePopoverHeader
              title="Invoiced"
              filters={filterParams}
              setFilters={setFilterParams}
              column="vendor_invoice_sum"
              popoverId="vendor-invoiced-sort"
              activePopoverId={activePopoverId}
              setActivePopoverId={setActivePopoverId}
            />
            <SortablePopoverHeader
              title="Paid"
              filters={filterParams}
              setFilters={setFilterParams}
              column="vendor_check_amount_sum"
              popoverId="vendor-paid-sort"
              activePopoverId={activePopoverId}
              setActivePopoverId={setActivePopoverId}
            />
          </tr>
        </thead>
        <tbody>
          {isLoading && <TableLoading columns={6} />}

          {isSuccess &&
            vendors.data.map((v) => (
              <tr
                key={`vendor-row-${v.id}`}
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/vendors/${v.id}`)}
              >
                <td>
                  <span className="me-2">{v.name}</span>
                  {v.is_pcard ? (
                    <span className="me-2">
                      <StatusBadge
                        title="PCard"
                        icon={faCreditCard}
                        tooltipText="Vendor generated from PCard file"
                      />
                    </span>
                  ) : (
                    <small className="text-muted">
                      ID: {v.external_id}
                      {v.status && (
                        <span className="text-muted ms-2">{v.status}</span>
                      )}
                    </small>
                  )}
                  <div className="mt-1">
                    {v.recent_payment_change && (
                      <VendorAttribute
                        title="Recent Payment Change"
                        tooltipText={`Vendor payment method changed in the last 30 days: ${v.last_payment_method_change_on}`}
                      />
                    )}
                    {v.stale && (
                      <VendorAttribute
                        title="Stale"
                        tooltipText={`Vendor has not sent invoice or received a check in ${municipality.stale_vendor_months} months. Last Invoice: ${v.last_invoice_on} Last Check: ${v.last_check_on}`}
                      />
                    )}
                    {v.po_box && (
                      <VendorAttribute
                        title="PO Box"
                        tooltipText={'Vendor has a PO Box address'}
                      />
                    )}
                    {v.one_eight_hundred && (
                      <VendorAttribute
                        title="1-800"
                        tooltipText={'Vendor has an 800 number'}
                      />
                    )}
                    {v.is_duplicate_ein && (
                      <VendorAttribute
                        title={
                          <span>
                            <FontAwesomeIcon icon={faClone} /> EIN
                          </span>
                        }
                        tooltipText={`Vendor has a duplicated EIN with another active vendor: ${v.duplicate_ein_vendors
                          .split('||')
                          .join(', ')}`}
                      />
                    )}
                    {v.is_duplicate_ein_being_paid && (
                      <VendorAttribute
                        title={
                          <span>
                            <FontAwesomeIcon icon={faClone} /> EIN $
                          </span>
                        }
                        tooltipText={`Multiple Vendors with the same EIN have been paid in the past year: ${v.duplicate_ein_being_paid_vendors
                          .split('||')
                          .join(', ')}`}
                      />
                    )}
                  </div>
                </td>
                <td className="align-middle">
                  <div>
                    <RiskBadge count={v.risk_rate} showCount={true} />
                  </div>
                </td>
                <td className="align-middle fw-bold">
                  {Math.floor(v.analytics_count)}
                </td>
                {/* TODO: <td data-sparkline={`${fourNumbers()}`}></td> */}
                <td className="text-muted align-middle">
                  {Math.floor(v.transactions_count)}
                </td>
                <td className="text-muted align-middle">
                  {noDigitMoney(v.vendor_invoice_sum)}
                </td>
                <td className="text-muted align-middle">
                  {v.is_pcard
                    ? noDigitMoney(v.transactions_amount)
                    : noDigitMoney(v.vendor_check_amount_sum)}
                </td>
              </tr>
            ))}
        </tbody>
      </SparkLineTable>
      {isSuccess && (
        <TableFooter
          meta={vendors.meta}
          params={filterParams}
          setParams={setFilterParams}
        />
      )}
    </>
  )
}

export default Table
